import axios, { type AxiosResponse } from 'axios';
import { API } from '@/api/api';
import type {
  LinkedModel,
  ModelGroupSpecification,
  ModelPriceMonitoringType,
  ModelSpecification,
  RequestParams,
  ResponseLinkedModels,
  ResponseModelBaseSpecs,
  ResponseModelGroupSpecs,
  ResponseModelOffers,
  ResponseModelReviews,
  ResponseModelSimilar,
} from '~/interfaces/api';
import { getModelsOffersList } from '~/api/global';
import { useGlobalStore } from '~/stores/global';
import { useAnalyticsStore } from '~/stores/analytics';
import type { ParamsReviewsQuestions } from '~/interfaces/app';

export async function getModel(model_id: string, params: RequestParams) {
  try {
    const globalStore = useGlobalStore();
    const analyticsStore = useAnalyticsStore();
    const controller = new AbortController();
    if (!import.meta.env.SSR) {
      globalStore.setCancelToken(controller);
    }
    const setParams = {
      ...params,
      sem: globalStore.sem,
      device: globalStore.device || 1,
      adult: globalStore.adult,
      split: analyticsStore?.additional?.split,
    };
    const res = await API.get(
      // получаем параметры модели
      `/models/${model_id}`,
      {
        params: { ...setParams },
        signal: controller.signal,
      }
    );
    const additional = res.data?.additional_images || [];
    return {
      ...res.data,
      additional_images: [res.data?.image, ...additional],
    };
  } catch (err) {
    if (axios.isCancel(err)) {
      return { cancelToken: true };
    }
    throw showError({ statusCode: 500 });
  }
}

export async function modelsSpecificationBase(
  model_id: string | number[]
): Promise<ResponseModelBaseSpecs> {
  const clearData = {
    show_description_in_modelcard: false,
    show_presets_anchor_in_modelcard: false,
    specifications: [],
  };
  try {
    const controller = new AbortController();
    if (!import.meta.env.SSR) {
      const globalStore = useGlobalStore();
      globalStore.setCancelToken(controller);
    }
    const res = await API.get(`/models/${model_id}/specification/base`, {
      signal: controller.signal,
    });
    // для совместимости с в3
    if ('specifications' in res?.data) return res?.data;
    return {
      show_description_in_modelcard: false,
      show_presets_anchor_in_modelcard: false,
      specifications: res?.data,
    };
  } catch (err) {
    return clearData;
  }
}

export async function modelsSpecification(
  model_id: string | number,
  isBot: boolean
): Promise<ResponseModelGroupSpecs> {
  try {
    const controller = new AbortController();
    if (!import.meta.env.SSR) {
      const globalStore = useGlobalStore();
      globalStore.setCancelToken(controller);
    }
    const res = await API.get(`/models/${model_id}/specification`, {
      signal: controller.signal,
    });
    const spec = res.data?.filter(
      (e: ModelGroupSpecification) => e.specifications.length
    );
    if (isBot)
      return spec.map((e: ModelGroupSpecification) => ({
        ...e,
        specifications: e.specifications.map((e2: ModelSpecification) => ({
          ...e2,
          description: '',
        })),
      }));
    return spec;
  } catch (err) {
    return [];
  }
}

export async function modelsReviews(
  model_id: number | string,
  params: ParamsReviewsQuestions
): Promise<ResponseModelReviews | undefined> {
  try {
    const controller = new AbortController();
    if (!import.meta.env.SSR) {
      const globalStore = useGlobalStore();
      globalStore.setCancelToken(controller);
    }
    const res = await API.get(`/models/${model_id}/reviews`, {
      params,
      signal: controller.signal,
    });
    return res.data;
  } catch (err) {
    return undefined;
  }
}

export async function modelsQuestions(
  model_id: number | string,
  params: ParamsReviewsQuestions
): Promise<ResponseModelReviews | undefined> {
  const controller = new AbortController();
  if (!import.meta.env.SSR) {
    const globalStore = useGlobalStore();
    globalStore.setCancelToken(controller);
  }
  const res = await API.get(`/models/${model_id}/questions`, {
    params,
    signal: controller.signal,
  }).catch(() => {
    return undefined;
  });
  return res?.data;
}

export async function getLinkedModels(
  model_id: number | string
): Promise<LinkedModel[]> {
  try {
    const controller = new AbortController();
    if (!import.meta.env.SSR) {
      const globalStore = useGlobalStore();
      globalStore.setCancelToken(controller);
    }
    const res: AxiosResponse<ResponseLinkedModels> = await API.get(
      `/models/${model_id}/linked_models`,
      { signal: controller.signal }
    );
    if (res?.data?.items) return res.data.items;
    return [];
  } catch (err) {
    return [];
  }
}

export async function photoAllReviews(model_id: string, params: RequestParams) {
  try {
    const controller = new AbortController();
    if (!import.meta.env.SSR) {
      const globalStore = useGlobalStore();
      globalStore.setCancelToken(controller);
    }
    const res = await API.get(`/models/${model_id}/reviews/images`, {
      params,
      signal: controller.signal,
    });
    return res.data;
  } catch (err) {
    return { list: [], per_page: 0, total: 0 };
  }
}

export async function modelsOffers(
  reqBody: ResponseModelOffers,
  model_id: string,
  params: RequestParams
) {
  try {
    return await getModelsOffersList(model_id, reqBody || null, params);
  } catch (err) {
    return null;
  }
}

export async function modelsAccessories(
  category_id: number | undefined,
  regionId: RequestParams,
  globalSem: string
) {
  const globalStore = useGlobalStore();
  const analyticsStore = useAnalyticsStore();
  const controller = new AbortController();
  if (!import.meta.env.SSR) {
    globalStore.setCancelToken(controller);
  }
  return await API.get(`/categories/${category_id}/accessories`, {
    params: {
      region_id: regionId || 1,
      sem: globalSem || undefined,
      device: globalStore.device || 1,
      adult: globalStore.adult,
      split: analyticsStore?.additional?.split,
    },
    signal: controller.signal,
  })
    .then((res) => {
      if (res && res?.data?.items && res.data?.items.length > 0) {
        return res.data.items;
      }
    })
    .catch((thrown) => {
      if (axios.isCancel(thrown)) {
        return { cancelToken: true };
      } else {
        return [];
      }
    });
}

export async function modelsOther(
  model_id: string,
  params: RequestParams
): Promise<ResponseModelSimilar> {
  try {
    const globalStore = useGlobalStore();
    const analyticsStore = useAnalyticsStore();
    const controller = new AbortController();
    if (!import.meta.env.SSR) {
      globalStore.setCancelToken(controller);
    }
    const res = await API.get(`/models/${model_id}/other`, {
      params: {
        ...params,
        device: globalStore.device,
        adult: globalStore.adult,
        split: analyticsStore?.additional?.split,
      },
      signal: controller.signal,
    });
    return res.data;
  } catch (err) {
    return [];
  }
}

export async function getModelMonitoring(
  params: RequestParams,
  id: string | number
): Promise<ModelPriceMonitoringType> {
  const controller = new AbortController();
  if (!import.meta.env.SSR) {
    const globalStore = useGlobalStore();
    globalStore.setCancelToken(controller);
  }
  const res = await API.get(`/models/${id}/price_monitoring`, {
    params,
    signal: controller.signal,
  })
    .then((data) => data)
    .catch(() => {
      return null;
    });
  return res?.data;
}
